// DS — glify stanów i nawigacji (artboard 09, MAPA decyzja 6).
//
// Osiem glifów rysowanych w jednym języku kreski z sygnetem: kreska 2.5 na
// siatce 48, zaokrąglone końce, jeden kolor (--text-muted; aktywny --accent),
// bez gradientu i wypełnień. Ścieżki 1:1 z
// `design/claude-design-wyniki/06-system/src/glify/*.svg`.
//
// Glify techniczne (`ustawienia`, a od sesji 12 także `autopilot` i `konto`)
// nie należą do zestawu ośmiu: istnieją wyłącznie dla nawigacji i stanów
// (suwaki Ustawień, edit z osią czasu dla Planera, sylwetka w kółku dla Twoich
// kont) i są rysowane tą samą kreską — MAPA 06 decyzja 6, MAPA 12 propozycja 5.
// Siatka `pulpit` wyleciała razem z ekranem (MINIMAL-2026-09-18): nic jej nie woła.
//
// Rozmiary: 24 (nawigacja), 48 (stan pusty, kafel), 96 (strona stanu).
// Lucide `Icon` zostaje wyłącznie dla akcji w treści (18 px, stroke 1.75).

import type { ReactElement, SVGProps } from 'react'

import { TOKEN_COIN, TOKEN_COIN_SMALL } from './tokenCoin'
import { PlatformLogo, platformLogoName } from './PlatformLogo'

export type GlyphName =
  | 'utwor' | 'klip' | 'edit' | 'seria' | 'hook' | 'opis' | 'gemy' | 'token' | 'token-s' | 'pomoc'
  | 'faktura'
  | 'ustawienia'
  | 'autopilot' | 'konto' | 'kalendarz' | 'menu' | 'staty' | 'blad'
  | 'tiktok' | 'instagram' | 'youtube'

const PATHS: Record<GlyphName, ReactElement> = {
  utwor: <path d="M10 20v8M17 14v20M24 8v32M31 16v16M38 21v6" />,
  klip: <><rect x="11" y="6" width="20" height="36" rx="4" /><path d="M37 12v24" /></>,
  edit: <><rect x="15" y="6" width="18" height="36" rx="4" /><path d="M20 26h8M22 31h4" /></>,
  seria: (
    <>
      <rect x="5" y="12" width="10" height="24" rx="2.5" />
      <rect x="19" y="12" width="10" height="24" rx="2.5" />
      <rect x="33" y="12" width="10" height="24" rx="2.5" />
    </>
  ),
  hook: <><rect x="6" y="11" width="36" height="22" rx="7" /><path d="M15 22h12M14 33l-3 7" /></>,
  opis: <path d="M8 13h32M8 24h32M8 35h18" />,
  gemy: <><path d="M12 10h24l8 10-20 22L4 20z" /><path d="M4 20h40M17 10l-5 10 12 22 12-22-5-10" /></>,
  // Waluta produktu (reguła 42 po arkuszu T2 i dopisku koordynatora nr 2): MONETA ED
  // — pełny krążek z nutami wyciętymi jako otwory. Rysunek stoi w `tokenCoin.ts`,
  // jedna stała na rozmiar: to TYMCZASOWY wariant 4 arkusza, a ostateczny (wariant 5)
  // podmieni koordynator, zmieniając tam dwa łańcuchy i nic więcej.
  //
  // Siatka rysunku to 24, ten zestaw ma 48, więc znak wchodzi przez `scale(2)`.
  // Przeskalowanie, a nie druga kopia ścieżki: dwie kopie rozjeżdżają się przy
  // pierwszej poprawce, a ta poprawka już jest zapowiedziana.
  token: (
    <g transform="scale(2)">
      <path d={TOKEN_COIN} fill="currentColor" fillRule="nonzero" stroke="none" />
    </g>
  ),
  // PONIŻEJ 20 px idzie `token-s`, nie ten rysunek: skalowanie siatki 24 do 16 px
  // zamyka otwory nut w plamę. Mała moneta ma własną geometrię (koła zamiast elips,
  // pojedyncza belka, luźniejsze otwory), na siatce 16 — stąd `scale(3)`.
  'token-s': (
    <g transform="scale(3)">
      <path d={TOKEN_COIN_SMALL} fill="currentColor" fillRule="nonzero" stroke="none" />
    </g>
  ),
  pomoc: (
    <>
      <circle cx="24" cy="24" r="18" />
      <path d="M18.5 19.5a5.5 5.5 0 1 1 7.5 5.1c-1.6.7-2 1.6-2 3.4" />
      <circle cx="24" cy="34" r="1.2" fill="currentColor" stroke="none" />
    </>
  ),
  ustawienia: (
    <>
      <path d="M8 15h32M8 24h32M8 33h32" />
      <circle cx="18" cy="15" r="3.5" fill="var(--glyph-knob, var(--bg-card))" />
      <circle cx="31" cy="24" r="3.5" fill="var(--glyph-knob, var(--bg-card))" />
      <circle cx="21" cy="33" r="3.5" fill="var(--glyph-knob, var(--bg-card))" />
    </>
  ),
  // edit (ramka 9:16) z osią czasu obok: trzy linie planu
  autopilot: (
    <>
      <rect x="7" y="11" width="14" height="26" rx="3.5" />
      <path d="M28 16h13M28 24h13M28 32h8" />
    </>
  ),
  // kartka kalendarza z trzema kropkami dni: strona Kalendarz (sesja 22, sprite g-kalendarz)
  kalendarz: (
    <>
      <rect x="7" y="10" width="34" height="31" rx="4" />
      <path d="M7 20h34M16 6v8M32 6v8" />
      <circle cx="17" cy="29" r="1.8" fill="currentColor" stroke="none" />
      <circle cx="24" cy="29" r="1.8" fill="currentColor" stroke="none" />
      <circle cx="31" cy="29" r="1.8" fill="currentColor" stroke="none" />
    </>
  ),
  // sylwetka w kółku: konto, które artysta sam podłączył
  konto: (
    <>
      <circle cx="24" cy="24" r="18" />
      <circle cx="24" cy="19.5" r="5.5" />
      <path d="M13.5 37c2.4-5.6 6.2-8.5 10.5-8.5s8.1 2.9 10.5 8.5" />
    </>
  ),
  // „Więcej” na dolnym pasku (Domi, 2026-09-17: „zamiast ? … 3 kreski jak te E z loga”):
  // trzy kreski w proporcjach sygnetu (paski 48 · 30 · 44) rozpięte na szerokości pozostałych
  // glifów kreskowych (`opis`: x 8→40, czyli 32 = 2/3 z 48 → 32 · 20 · 29), rozstaw 10 jak
  // w `opis`; kreska 2.5 jak reszta zestawu, bez wypełnień. Sygnet sam w sobie jest wypełniony
  // i grubszy — tu chodzi o rytm długości, nie o kopię.
  // R5: DWIE kreski różnej długości (18 i 11 jednostek siatki 24, artboard 02m),
  // nie trzy — trzy kreski to hamburger, którego ten produkt nie ma; to jest znak
  // panelu bocznego, ten sam co w znaku EDIMI. Ścieżka z `#i-menu`.
  menu: <path d="M6 17h36M6 31h22" />,
  // Loga platform NIE SĄ już rysowane tutaj (paczka P1 A, 2026-09-21). Sesja 20 R2 dała im
  // jednokolorowe glify kreskowe w `currentColor`; właściciel 21.09 wieczór poprosił o „normalne
  // mini loga, ich kolory przyciągają uwagę z daleka”, więc rysunek przeniósł się do
  // `PlatformLogo` — jedno źródło dla aplikacji i strony publicznej. Nazwy zostają w tym
  // zestawie, żeby ~20 miejsc wołających `<Glyph name="tiktok" …>` nie musiało się zmieniać:
  // `Glyph` oddaje je niżej `PlatformLogo`, a te wpisy trzymają tylko typ.
  tiktok: <></>,
  instagram: <></>,
  youtube: <></>,
  // sesja 38 („Do dopisania do języka”): pusty stan listy płatności. Rachunek
  // z wyciętym dołem i dwiema liniami pozycji — ta sama kreska 2.5 co reszta
  // zestawu, ścieżka przeskalowana z `g-faktura` w `38-…/src/glify38.svg`.
  faktura: <><path d="M12 6h24v36l-6-4-6 4-6-4-6 4z" /><path d="M18 16h12M18 24h12" /></>,
  // Sesja 36: słupki (zakładka Statystyki) i koło z wykrzyknikiem (błąd wczytania w rozmiarze
  // pustego stanu — zestaw sesji 30 nie ma glifu błędu). Ta sama kreska 2.5 na siatce 48.
  staty: <path d="M10 40V24M20 40V10M30 40V19M40 40V29" />,
  blad: <><circle cx="24" cy="24" r="18" /><path d="M24 14v13M24 33v1" /></>,
}

export const GLYPH_NAMES = Object.keys(PATHS) as GlyphName[]

export interface GlyphProps extends Omit<SVGProps<SVGSVGElement>, 'name'> {
  name: GlyphName
  /** 24 (nawigacja) · 48 (stan) · 96 (strona stanu). */
  size?: 24 | 48 | 96 | number
  /** `active` = --accent (pozycja wybrana); `current` = kolor tekstu obok. */
  tone?: 'muted' | 'active' | 'current'
  /** Etykieta dostępna, gdy glif stoi sam. Bez niej glif jest dekoracją (aria-hidden). */
  label?: string
}

export function Glyph({ name, size = 48, tone = 'muted', label, className = '', ...rest }: GlyphProps) {
  // Platforma = logo marki, nie kreska (paczka P1 A). `tone` i `strokeWidth` przestają
  // znaczyć cokolwiek dla tych trzech nazw: znak niesie własne barwy.
  const platform = platformLogoName(name)
  if (platform) {
    const { tone: _tone, strokeWidth: _strokeWidth, ...logoRest } = rest as Record<string, unknown>
    void _tone; void _strokeWidth
    return <PlatformLogo name={platform} size={size} label={label} className={className} {...logoRest} />
  }
  const cls = ['glyph', tone === 'active' && 'is-active', tone === 'current' && 'tone-current', className]
    .filter(Boolean)
    .join(' ')
  return (
    <svg
      xmlns="http://www.w3.org/2000/svg"
      viewBox="0 0 48 48"
      width={size}
      height={size}
      fill="none"
      stroke="currentColor"
      strokeWidth={2.5}
      strokeLinecap="round"
      strokeLinejoin="round"
      className={cls}
      role={label ? 'img' : undefined}
      aria-label={label}
      aria-hidden={label ? undefined : true}
      data-glyph={name}
      {...rest}
    >
      {PATHS[name]}
    </svg>
  )
}

export default Glyph
